import { useEffect, useState } from 'react';
import type { Flashcard } from '@/shared/types';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Tag } from '@/components/Badge';

const RATING_LABELS: Record<1 | 2 | 3 | 4, { label: string; hint: string; color: string }> = {
  1: { label: 'Again', hint: '1', color: 'bg-red-600 hover:bg-red-700' },
  2: { label: 'Hard', hint: '2', color: 'bg-orange-500 hover:bg-orange-600' },
  3: { label: 'Good', hint: '3', color: 'bg-green-600 hover:bg-green-700' },
  4: { label: 'Easy', hint: '4', color: 'bg-blue-600 hover:bg-blue-700' },
};

export function CardReview({
  card,
  onRate,
}: {
  card: Flashcard;
  onRate: (rating: 1 | 2 | 3 | 4, durationMs: number) => void;
}) {
  const [revealed, setRevealed] = useState(false);
  const [start, setStart] = useState(Date.now());

  useEffect(() => {
    setRevealed(false);
    setStart(Date.now());
  }, [card.id]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === ' ') {
        e.preventDefault();
        setRevealed(true);
        return;
      }
      if (!revealed) return;
      const n = Number(e.key);
      if ([1, 2, 3, 4].includes(n)) {
        e.preventDefault();
        onRate(n as 1 | 2 | 3 | 4, Date.now() - start);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [revealed, onRate, start]);

  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-wide text-slate-500">
          <span>Front · {card.bloom_level}</span>
          {card.tags.map((t) => (
            <Tag key={t}>{t}</Tag>
          ))}
        </div>
        <p className="text-base leading-relaxed text-slate-900">{card.front}</p>
      </Card>

      {revealed ? (
        <>
          <div className="rounded border border-slate-200 bg-slate-50 p-4">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">Back</div>
            <p className="text-base leading-relaxed text-slate-900">{card.back}</p>
            {card.explanation && (
              <p className="mt-3 text-sm text-slate-700">{card.explanation}</p>
            )}
            {card.mnemonic && (
              <p className="mt-3 rounded bg-yellow-50 p-2 text-sm italic text-slate-700">
                Mnemonic: {card.mnemonic}
              </p>
            )}
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[1, 2, 3, 4].map((r) => {
              const info = RATING_LABELS[r as 1 | 2 | 3 | 4];
              return (
                <button
                  key={r}
                  onClick={() => onRate(r as 1 | 2 | 3 | 4, Date.now() - start)}
                  className={`min-h-[44px] rounded px-2 py-2 text-sm font-medium text-white ${info.color}`}
                >
                  {info.label}
                  <span className="ml-1 text-xs opacity-80">[{info.hint}]</span>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <Button variant="secondary" size="lg" block className="py-3" onClick={() => setRevealed(true)}>
          Show answer <span className="text-xs text-slate-500">[space]</span>
        </Button>
      )}
    </div>
  );
}
